/**
 * The Figure Co. Homepage — signed-out front door.
 *
 * Composition: Header → Hero → seats (SeatSelector + SeatPanel, local
 * state, default 'attorney') → TrustLayer → How it works → closing CTA →
 * Footer. Design-law compliant: no orbs, no gradients, no blur, no
 * entrance animations. The old stats card, feature grids, orange CTA
 * band, and RecoveryEstimator are removed — one spine, seats self-select,
 * depth lives behind "how it works".
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import Hero from '../components/home/Hero';
import ProofBand from '../components/home/ProofBand';
import SeatSelector from '../components/home/SeatSelector';
import SeatPanel from '../components/home/SeatPanel';
import ArchitectureShowcase from '../components/home/ArchitectureShowcase';
import DeliverableShowcase from '../components/home/DeliverableShowcase';
import TrustLayer from '../components/home/TrustLayer';
import SecurityNote from '../components/home/SecurityNote';
import { ExhibitCell, SectionHeader } from '../components/home/sectionPrimitives';
import { CARRIER_NOTE, DEFAULT_SEAT_ID, getSeat } from '../config/seats.config';
import type { SeatId } from '../config/seats.config';
import { FigureCoWordmark } from '../components/brand/FigureCoLogo';

const NAV_LINK_CLASS =
  'font-body text-sm font-medium tracking-tight text-text-secondary transition-colors hover:text-text-primary';

const FOOTER_LINK_CLASS =
  'font-body text-sm text-text-muted transition-colors hover:text-text-primary';

const HOW_IT_WORKS_STEPS: Array<{ n: string; title: string; body: string }> = [
  {
    n: '01',
    title: 'Upload the books once',
    body: 'Financial statements, P&Ls, tax returns, leases — uploaded once. Whatever is missing, the platform requests from the client automatically.',
  },
  {
    n: '02',
    title: 'The right humans sign off',
    body: 'Your accountant approves every engine; your attorney locks and stamps the sworn number. Nothing leaves without a named human on the record.',
  },
  {
    n: '03',
    title: 'Serve court-ready deliverables',
    body: 'Demand letter, sworn Proof of Loss, court-ready PDF — each generated in minutes, every figure traceable to its source document.',
  },
];

export default function HomePage() {
  const [activeSeatId, setActiveSeatId] = useState<SeatId>(DEFAULT_SEAT_ID);
  const activeSeat = getSeat(activeSeatId);

  return (
    <div className="min-h-screen bg-base font-body">
      <SiteHeader />

      <main>
        <Hero />

        <ProofBand />

        {/* How it works — the 3-step overview sits high (above the seats) so a
            first-time visitor gets the whole arc before drilling into personas. */}
        <HowItWorks />

        {/* Seats — the nav "Features" anchor lands here: what the platform
            does, told per seat instead of as a feature list. */}
        <section id="features" className="scroll-mt-24">
          <div className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 lg:px-12 lg:py-24">
            {/* Cream, not orange: keeps the seat eyebrow below as the only orange
                atom in this viewport (K3 — no stacked orange eyebrows). */}
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-text-dim">
              Who it is for
            </p>
            <h2 className="mt-4 max-w-2xl font-heading text-3xl leading-tight tracking-tight text-text-primary md:text-4xl">
              One number, six seats. Pick yours.
            </h2>
            <div className="mt-8 lg:mt-10">
              <SeatSelector activeId={activeSeatId} onSelect={setActiveSeatId} />
            </div>
            <SeatPanel seat={activeSeat} />
          </div>
        </section>

        <ArchitectureShowcase />

        <DeliverableShowcase />

        <TrustLayer />

        <SecurityNote />

        {/* Closing CTA — one quiet strip, two actions (primary + human path). */}
        <section className="border-t border-hairline">
          <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-5 py-14 sm:px-8 md:flex-row md:items-center md:justify-between lg:px-12 lg:py-20">
            <div>
              <h2 className="font-heading text-2xl leading-tight tracking-tight text-text-primary md:text-3xl">
                Be among the first firms on the platform.
              </h2>
              <p className="mt-2 font-body text-sm text-text-secondary">
                We're onboarding a small group of plaintiff firms and CPAs in private beta.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                className="!rounded-none !bg-brand px-7 font-mono text-xs font-semibold uppercase tracking-[0.14em] shadow-none hover:!bg-brand-hover"
                style={{ color: 'var(--color-on-brand)' }}
              >
                <Link to="/waitlist">
                  Request early access
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="!rounded-none border-hairline bg-transparent px-6 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-text-primary shadow-none hover:bg-elevated"
              >
                <Link to="/contact">Contact us</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

/** How it works — the three-step spine (the reference molecule for the sections). */
function HowItWorks() {
  return (
    <section id="how-it-works" className="scroll-mt-24 border-t border-hairline">
      <div className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 lg:px-12 lg:py-24">
        <SectionHeader eyebrow="How it works" title="From paperwork to proof in three steps" />
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {HOW_IT_WORKS_STEPS.map((step) => (
            <ExhibitCell key={step.n} index={step.n} title={step.title} body={step.body} />
          ))}
        </div>
      </div>
    </section>
  );
}

/** Brand lockup — the code logo (theme-aware via tokens, no raster). */
function Wordmark({ compact = false }: { compact?: boolean }) {
  return <FigureCoWordmark size={compact ? 'sm' : 'lg'} />;
}

// Header CTA treatment — square mono caps both ways. Ghost while the hero's own
// orange CTA is on screen (so only ONE filled-orange CTA per viewport, K3), then
// fills once the hero scrolls out.
const HEADER_CTA_FILLED =
  '!rounded-none !bg-brand font-mono text-xs font-semibold uppercase tracking-[0.14em] shadow-none hover:!bg-brand-hover';
const HEADER_CTA_GHOST =
  '!rounded-none border border-hairline !bg-transparent font-mono text-xs font-semibold uppercase tracking-[0.14em] text-text-primary shadow-none hover:!bg-elevated';

function SiteHeader() {
  const [heroInView, setHeroInView] = useState(true);

  useEffect(() => {
    const el = document.getElementById('hero');
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(([entry]) => setHeroInView(entry.isIntersecting), {
      rootMargin: '-88px 0px 0px 0px',
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const ctaClass = heroInView ? HEADER_CTA_GHOST : HEADER_CTA_FILLED;
  const ctaStyle = heroInView ? undefined : { color: 'var(--color-on-brand)' };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-hairline bg-base">
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-8 lg:px-12">
        <Link to="/" className="flex items-center" aria-label="The Figure Co. home">
          <Wordmark />
        </Link>

        {/* Desktop nav */}
        <ul className="hidden items-center gap-6 md:flex lg:gap-8">
          <li>
            <a href="#features" className={NAV_LINK_CLASS}>
              Who it's for
            </a>
          </li>
          <li>
            <a href="#architecture" className={NAV_LINK_CLASS}>
              Method
            </a>
          </li>
          <li>
            <Link to="/about" className={NAV_LINK_CLASS}>
              About
            </Link>
          </li>
          <li>
            <Link to="/sign-in" className={NAV_LINK_CLASS}>
              Sign in
            </Link>
          </li>
          <li>
            <Button asChild className={ctaClass} style={ctaStyle}>
              <Link to="/waitlist">Request early access</Link>
            </Button>
          </li>
        </ul>

        {/* Phone nav — one primary action; Sign in moves into /sign-in direct. */}
        <div className="flex items-center md:hidden">
          <Button asChild size="sm" className={ctaClass} style={ctaStyle}>
            <Link to="/waitlist">Request early access</Link>
          </Button>
        </div>
      </nav>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-hairline">
      <div className="mx-auto max-w-[1400px] px-5 py-12 sm:px-8 lg:px-12">
        <div className="mb-12 grid gap-10 md:grid-cols-4">
          <div>
            <div className="mb-4">
              <Wordmark compact />
            </div>
            <p className="font-body text-sm text-text-muted">
              Forensic business-interruption claim calculations for attorneys,
              accountants, and the people who have to defend the number.
            </p>
          </div>
          <div>
            <h4 className="mb-4 font-body font-semibold text-text-primary">Product</h4>
            <ul className="space-y-2">
              <li>
                <a href="#features" className={FOOTER_LINK_CLASS}>
                  Who it is for
                </a>
              </li>
              <li>
                <a href="#how-it-works" className={FOOTER_LINK_CLASS}>
                  How it works
                </a>
              </li>
              <li>
                <a href="#trust" className={FOOTER_LINK_CLASS}>
                  Why the number holds
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="mb-4 font-body font-semibold text-text-primary">Company</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className={FOOTER_LINK_CLASS}>
                  About us
                </Link>
              </li>
              <li>
                <Link to="/contact" className={FOOTER_LINK_CLASS}>
                  Contact
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="mb-4 font-body font-semibold text-text-primary">Legal</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy" className={FOOTER_LINK_CLASS}>
                  Privacy policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className={FOOTER_LINK_CLASS}>
                  Terms of service
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <p className="border-t border-hairline pt-8 font-mono text-[11px] leading-relaxed text-text-dim">
          Tamper-evident audit ledger · encrypted in transit · isolated per firm · never used to
          train AI · architected for SOC&nbsp;2 (certification on our roadmap).
        </p>
        <p className="mt-3 font-body text-[11px] leading-relaxed text-text-dim">
          The Figure Co. is a software platform — not a law firm or CPA firm. Every figure is
          attested by your own licensed professionals.
        </p>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-body text-sm text-text-dim">
            © {new Date().getFullYear()} The Figure Co. All rights reserved.
          </p>
          {/* Standing HOLD: one soft carrier line, no signup, no route. */}
          <Link
            to={CARRIER_NOTE.href}
            className="font-mono text-xs text-text-dim transition-colors hover:text-text-secondary"
          >
            {CARRIER_NOTE.label}
          </Link>
        </div>
      </div>
    </footer>
  );
}
